import React, { useContext, useState } from "react";
import PropTypes from "prop-types";

import AddCircleOutlineIcon from "@material-ui/icons/Add";
import {
  Grid,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Popover
} from "@material-ui/core";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { BACKEND_URL } from "../../config/env";
import ConfirmationModal from "../ConfirmationModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import EditMessageModal from "../EditMessageModal";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import ForwardModal from "../../components/ForwardMessageModal";
import SaveFieldFromTextMenu from "../SaveFieldFromTextMenu";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(() => ({
  iconButton: {
    padding: "4px"
  },
  gridContainer: {
    padding: "10px",
    justifyContent: "center"
  },
  addCircleButton: {
    padding: "8px",
    fontSize: "2rem",
    backgroundColor: "rgb(242 242 247)"
  },
  popoverContent: {
    maxHeight: "300px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "0.4em",
      height: "0.4em"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,.1)",
      borderRadius: "50px"
    },
    "&::-webkit-scrollbar-track": {
      boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
      webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)"
    }
  },
  hideScrollbar: {
    maxHeight: "300px",
    overflow: "hidden"
  }
}));

const AVAILABLE_REACTIONS = [
  "😀", "😂", "❤️", "👍", "🎉", "😢", "😮", "😡", "👏", "🔥",
  "🥳", "😎", "🤩", "😜", "🤔", "🙄", "😴", "😇", "🤯", "💩",
  "🤗", "🤫", "🤭", "🤓", "🤪", "🤥", "🤡", "🤠", "🤢", "🤧",
  "😷", "🤕", "🤒", "👻", "💀", "☠️", "👽", "👾", "🤖", "🎃",
  "😺", "😸", "😹", "😻", "😼", "😽", "🙀", "😿", "😾", "🙈",
  "🙉", "🙊", "🐵", "🐒", "🦍", "🐶", "🐕", "🐩", "🐺", "🦊",
  "🦝", "🐱", "🐈", "🦁", "🐯", "🐅", "🐆", "🐴", "🐎", "🦄"
];

const getAnchorPositionFromTarget = target => {
  const rect = target?.getBoundingClientRect?.();

  if (!rect) {
    return { top: 0, left: 0 };
  }

  return {
    top: Math.round(rect.bottom + 8),
    left: Math.round(rect.left)
  };
};

const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl, contactId, onOpenCoachNotes }) => {
  const classes = useStyles();
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const {
    showSelectMessageCheckbox,
    setShowSelectMessageCheckbox,
    selectedMessages,
    forwardMessageModalOpen,
    setForwardMessageModalOpen
  } = useContext(ForwardMessageContext);

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationEditOpen, setEditMessageOpenModal] = useState(false);
  const [messageEdit, setMessageEdit] = useState(false);
  const [reactionAnchorPosition, setReactionAnchorPosition] = useState(null);
  const [moreAnchorPosition, setMoreAnchorPosition] = useState(null);
  const [saveFieldOpen, setSaveFieldOpen] = useState(false);
  const [selectedText, setSelectedText] = useState("");

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const openReactionsMenu = event => {
    setReactionAnchorPosition(getAnchorPositionFromTarget(event.currentTarget));
    handleClose();
  };

  const closeReactionsMenu = () => {
    setReactionAnchorPosition(null);
  };

  const openMoreReactionsMenu = () => {
    setMoreAnchorPosition(reactionAnchorPosition);
    closeReactionsMenu();
  };

  const closeMoreReactionsMenu = () => {
    setMoreAnchorPosition(null);
  };

  const closeAllMenus = () => {
    handleClose();
    closeReactionsMenu();
    closeMoreReactionsMenu();
  };

  const handleReactToMessage = async reactionType => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/messages/${message.id}/reactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ type: reactionType })
      });

      if (!response.ok) {
        let errorMessage = i18n.t("backendErrors.ERR_GENERIC_ERROR");
        try {
          const payload = await response.json();
          errorMessage = payload?.message || payload?.error || errorMessage;
        } catch (_error) {
          // Keep generic message when backend does not return JSON.
        }
        throw new Error(errorMessage);
      }

      toast.success(i18n.t("reactions.successMessage"));
    } catch (err) {
      toastError(err);
    }

    closeAllMenus();
  };

  const handleSaveToContact = () => {
    const selection = window.getSelection();
    const text =
      selection && selection.toString().trim()
        ? selection.toString().trim()
        : message.body || "";

    setSelectedText(text);
    setSaveFieldOpen(true);
    handleClose();
  };

  const handleOpenCoachNotes = () => {
    if (onOpenCoachNotes) {
      onOpenCoachNotes(message);
    }
    handleClose();
  };

  const handleSetShowSelectCheckbox = () => {
    setShowSelectMessageCheckbox(!showSelectMessageCheckbox);
    handleClose();
  };

  const handleEditMessage = async () => {};

  const handleReplyMessage = () => {
    setReplyingMessage(message);
    handleClose();
  };

  const handleOpenConfirmationModal = () => {
    setConfirmationOpen(true);
    handleClose();
  };

  const handleOpenEditMessageModal = () => {
    setEditMessageOpenModal(true);
    setMessageEdit(message);
    handleClose();
  };

  return (
    <>
      <ForwardModal
        modalOpen={forwardMessageModalOpen}
        messages={selectedMessages}
        onClose={() => {
          setForwardMessageModalOpen(false);
          setShowSelectMessageCheckbox(false);
        }}
      />

      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>

      <EditMessageModal
        title={i18n.t("messageOptionsMenu.editMessageModal.title")}
        open={confirmationEditOpen}
        onClose={setEditMessageOpenModal}
        onSave={handleEditMessage}
        message={messageEdit}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </EditMessageModal>

      <Menu
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={menuOpen}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSetShowSelectCheckbox}>
          {i18n.t("messageOptionsMenu.forward")}
        </MenuItem>

        {message.fromMe && (
          <MenuItem onClick={handleOpenEditMessageModal}>
            {i18n.t("messageOptionsMenu.edit")}
          </MenuItem>
        )}

        {message.fromMe && (
          <MenuItem onClick={handleOpenConfirmationModal}>
            {i18n.t("messageOptionsMenu.delete")}
          </MenuItem>
        )}

        <MenuItem onClick={handleReplyMessage}>
          {i18n.t("messageOptionsMenu.reply")}
        </MenuItem>

        <MenuItem onClick={openReactionsMenu}>
          {i18n.t("messageOptionsMenu.react")}
        </MenuItem>

        <MenuItem onClick={handleOpenCoachNotes}>
          Anotación interna
        </MenuItem>

        {contactId && (
          <MenuItem onClick={handleSaveToContact}>
            {i18n.t("messageOptionsMenu.saveToContact")}
          </MenuItem>
        )}
      </Menu>

      <SaveFieldFromTextMenu
        open={saveFieldOpen}
        onClose={() => setSaveFieldOpen(false)}
        selectedText={selectedText}
        contactId={contactId}
      />

      <Popover
        open={Boolean(reactionAnchorPosition)}
        anchorReference="anchorPosition"
        anchorPosition={reactionAnchorPosition}
        onClose={closeReactionsMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        PaperProps={{
          style: { width: "auto", maxWidth: "380px", borderRadius: "50px" }
        }}
      >
        <div className={classes.hideScrollbar}>
          <Grid container spacing={1} className={classes.gridContainer}>
            {AVAILABLE_REACTIONS.slice(0, 6).map(reaction => (
              <Grid item key={reaction}>
                <IconButton
                  className={classes.iconButton}
                  onClick={() => handleReactToMessage(reaction)}
                >
                  {reaction}
                </IconButton>
              </Grid>
            ))}

            <Grid item>
              <IconButton
                className={classes.addCircleButton}
                onClick={openMoreReactionsMenu}
              >
                <AddCircleOutlineIcon fontSize="normal" />
              </IconButton>
            </Grid>
          </Grid>
        </div>
      </Popover>

      <Popover
        open={Boolean(moreAnchorPosition)}
        anchorReference="anchorPosition"
        anchorPosition={moreAnchorPosition}
        onClose={closeMoreReactionsMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        PaperProps={{
          style: { width: "auto", maxWidth: "400px", borderRadius: "6px" }
        }}
      >
        <div className={classes.popoverContent}>
          <Grid container spacing={1} className={classes.gridContainer}>
            {AVAILABLE_REACTIONS.map(reaction => (
              <Grid item key={reaction}>
                <IconButton
                  className={classes.iconButton}
                  onClick={() => handleReactToMessage(reaction)}
                >
                  {reaction}
                </IconButton>
              </Grid>
            ))}
          </Grid>
        </div>
      </Popover>
    </>
  );
};

MessageOptionsMenu.propTypes = {
  message: PropTypes.object,
  menuOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.object,
  onReaction: PropTypes.func,
  availableReactions: PropTypes.arrayOf(PropTypes.string),
  onOpenCoachNotes: PropTypes.func
};

export default MessageOptionsMenu;
